// Re-export RTK Query hooks with cleaner names
export {
  useGetAppointmentDetailsQuery,
  useGetEncounterDetailsQuery,
  useGetFeaturesListQuery,
  useGetNoteDetailsQuery,
  useGetOrganizationProvidersQuery,
  useGetProviderAppointmentsQuery,
  useGetProviderByIdQuery,
  useGetProviderEncountersQuery,
  useGetProviderInfoQuery,
  useGetProviderNotesQuery,
  useGetProviderQuery,
  useGetProviderTranscriptionsQuery,
  useGetTranscriptDetailsQuery,
  useGetUserDetailsQuery,
  useSearchProvidersQuery,
  useUpdateProviderFeatureFlagMutation,
  useUpdateProviderProfileMutation,
} from './api';

// Import admin and organizations API from generated code
import { enhancedApi as adminApi } from '@store/generated/admin';
import { enhancedApi as organizationsApi } from '@store/generated/organizations';

// Re-export data-migration hooks from admin API object
export const {
  useBulkUpdatePatientStatusMutation,
  useDeleteImportSessionMutation,
  useExportImportResultsQuery,
  useGetImportOperationStatusQuery,
  useGetImportSessionStatusQuery,
  useGetMatchedPatientsQuery,
  useImportSelectedPatientsMutation,
  useImportUnmatchedPatientsMutation,
  useListImportOperationsQuery,
  useListOrganizationImportSessionsQuery,
  useMergeMultiplePatientsMutation,
  useRejectPatientsMutation,
} = adminApi;

// Re-export uploadImportFile from our custom implementation
export { useUploadImportFileMutation } from './api';

// Re-export Prospects (Sales Data) hooks
export {
  useGetProspectByIdQuery,
  useGetProspectsQuery,
  useUploadCsvProspectsMutation,
} from './api';

// Re-export Eligibility Workqueue hooks
export {
  useGetAdminEligibilityListQuery,
  useAdminOverrideEligibilityMutation,
} from './api';

// Re-export organizations API hooks with user-friendly names
export const {
  useGetV2AdminOrganizationsQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  usePatchV2AdminOrganizationsByOrganizationIdMutation,
  useGetV2AdminOrganizationsByOrganizationIdProvidersQuery,
  usePostV2AdminOrganizationsByOrganizationIdMembersMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdMembersAndUserIdMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdReactivateMutation,
  usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdSyncSubscriptionMutation,
  useGetV2AdminOrganizationsByOrganizationIdSubscriptionDashboardUrlQuery,
  usePostV2AdminOrganizationsByOrganizationIdExtendTrialMutation,
  usePostV2AdminOrganizationsByOrganizationIdCreateSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdMergeMutation,
  // Templates and Forms
  useGetV2AdminOrganizationsByOrganizationIdTemplatesQuery,
  usePostV2AdminOrganizationsByOrganizationIdTemplatesMutation,
  usePutV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
  useGetV2AdminOrganizationsByOrganizationIdFormsQuery,
  usePostV2AdminOrganizationsByOrganizationIdFormsMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdFormsAndFormIdMutation,
  // Patient Visibility
  useGetV2AdminOrganizationsByOrganizationIdPatientVisibilityQuery,
  usePatchV2AdminOrganizationsByOrganizationIdPatientVisibilityMutation,
  // Data Security Admin
  useGetV2AdminOrganizationsByOrganizationIdDataSecurityQuery,
  usePutV2AdminOrganizationsByOrganizationIdBaaMutation,
  usePutV2AdminOrganizationsByOrganizationIdCoiMutation,
  usePutV2AdminOrganizationsByOrganizationIdDataRetentionMutation,
  // Membership Info (co-sign settings)
  useGetV2AdminOrganizationsByOrganizationIdMembersAndUserIdMembershipInfoQuery:
    useGetAdminMembershipInfoQuery,
  usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdMembershipInfoMutation:
    useUpdateAdminMembershipInfoMutation,
  // Invitations
  useGetV2AdminOrganizationsByOrganizationIdInvitationsQuery: useGetAdminInvitationsQuery,
  usePostV2AdminOrganizationsByOrganizationIdInvitationsMutation: useCreateAdminInvitationMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdInvitationsAndInvitationIdMutation: useDeleteAdminInvitationMutation,
  usePostV2AdminOrganizationsByOrganizationIdInvitationsAndInvitationIdResendMutation: useResendAdminInvitationMutation,
} = organizationsApi;

// Re-export admin encounter types hooks with user-friendly names
export const {
  useGetV2AdminProvidersByProviderIdEncounterTypesQuery: useGetAdminProviderEncounterTypesQuery,
  usePostV2AdminProvidersByProviderIdEncounterTypesMutation: useCreateAdminProviderEncounterTypeMutation,
  usePatchV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeIdMutation: useUpdateAdminProviderEncounterTypeMutation,
  useDeleteV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeIdMutation: useDeleteAdminProviderEncounterTypeMutation,
  useGetV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeIdQuery: useGetAdminProviderEncounterTypeQuery,
} = adminApi;

// Re-export admin template discovery API hooks with user-friendly names
export const {
  useGetV2AdminTemplatesUserTemplatesQuery: useGetUserTemplatesQuery,
  // Capabilities hooks (read-only - mutations removed with backend endpoints)
  useGetV2AdminCapabilitiesQuery: useGetCapabilitiesQuery,
  useGetV2AdminCapabilitiesByCapabilityIdQuery: useGetCapabilityQuery,
  // Bundles hooks (read-only - mutations removed with backend endpoints)
  useGetV2AdminBundlesQuery: useGetBundlesQuery,
  useGetV2AdminBundlesByBundleIdQuery: useGetBundleQuery,
  // Bundle assignments hooks
  useGetV2AdminBundleAssignmentsQuery: useGetBundleAssignmentsQuery,
  usePostV2AdminBundleAssignmentsMutation: useCreateBundleAssignmentMutation,
  useGetV2AdminBundleAssignmentsByAssignmentIdQuery: useGetBundleAssignmentQuery,
  usePutV2AdminBundleAssignmentsByAssignmentIdStatusMutation: useUpdateBundleAssignmentStatusMutation,
  usePostV2AdminBundleAssignmentsByAssignmentIdReplaceMutation: useReplaceBundleAssignmentMutation,
  // Bundle detail assignments hook (lists all assignments for a specific bundle)
  useGetV2AdminBundlesByBundleIdAssignmentsQuery: useGetBundleDetailAssignmentsQuery,
  // Entitlements hooks
  useGetV2AdminEntitlementsQuery: useGetEntitlementsQuery,
  useGetV2AdminEntitlementsUsageQuery: useGetEntitlementsUsageQuery,
} = adminApi;

// Re-export admin build hooks with user-friendly names
export const {
  usePostV2AdminNotetemplatesBuildFromTemplateMutation: useAdminBuildFromTemplateMutation,
  useGetV2AdminNotetemplatesBuildByTaskIdStatusQuery: useAdminBuildStatusQuery,
  usePostV2AdminNotetemplatesBuildByTaskIdSaveMutation: useAdminBuildSaveMutation,
} = adminApi;

// Re-export Stedi transaction hooks with user-friendly names
import { enhancedApi as stediApi } from '@store/generated/stedi';

export const {
  useGetV2AdminOrgsByOrganizationIdStediTransactionsQuery: useStediTransactionsQuery,
  useGetV2AdminOrgsByOrganizationIdStediTransactionsAndTransactionIdQuery: useStediTransactionDetailQuery,
} = stediApi;

// Re-export Remittance hooks with user-friendly names
import { enhancedApi as billingApi } from '@store/generated/billing';

export const {
  useGetV2AdminOrgsByOrganizationIdRemittancesQuery: useRemittancesQuery,
  useGetV2AdminOrgsByOrganizationIdRemittancesAndRemittanceIdQuery: useRemittanceDetailQuery,
  useGetV2AdminOrgsByOrganizationIdRemittancesClaimsAndClaimIdSummaryQuery: useClaimRemittanceSummaryQuery,
} = billingApi;

// Re-export Billing Dashboard hooks with user-friendly names
export const {
  useGetV2AdminBillingDashboardOrganizationsQuery: useBillingDashboardOrgsQuery,
  useGetV2AdminBillingDashboardOrganizationsByOrganizationIdQuery: useBillingDashboardOrgDetailQuery,
} = billingApi;

// Import templates API for model conversion hooks
import { enhancedApi as templatesApi } from '@store/generated/templates';

// Re-export model conversion hooks with user-friendly names
export const {
  usePostV2AdminNotetemplatesModelConversionStartMutation: useStartModelConversionMutation,
  useGetV2AdminNotetemplatesModelConversionStatusByThreadIdQuery: useModelConversionStatusQuery,
  usePostV2AdminNotetemplatesModelConversionSaveMutation: useSaveModelConversionMutation,
  usePostV2AdminNotetemplatesModelConversionCopyToNewMutation: useCopyModelConversionMutation,
} = templatesApi;

// Re-export admin note templates API hooks
export {
  type AdminConvertToOrgRequest,
  type AdminCopyNoteTemplateRequest,
  type AdminCopyNoteTemplateResponse,
  type AdminCreateOrgTemplateRequest,
  type AdminDeleteTemplateResponse,
  type AdminGetNoteTemplateResponse,
  type AdminGetRevisionResponse,
  type AdminListRevisionsResponse,
  // Types
  type AdminNoteTemplateInfo,
  type AdminNoteTemplateListResponse,
  type AdminNoteTemplateSectionRequest,
  type AdminProviderTemplateListResponse,
  type AdminUpdateNoteTemplateRequest,
  type AdminUpdateNoteTemplateResponse,
  // Provider templates types
  type EffectiveNoteTemplateSummary,
  type NoteTemplate2,
  type NoteTemplateOwnerScope,
  type NoteTemplateRevision2,
  type NoteTemplateSection2,
  type RevisionDetail,
  // Revision history types
  type RevisionSummary,
  useConvertAdminNoteTemplateToOrgMutation,
  useCopyAdminNoteTemplateMutation,
  useCreateAdminOrgNoteTemplateMutation,
  useDeleteAdminNoteTemplateMutation,
  // Fork template for user hook (available after codegen)
  useForkTemplateForUserMutation,
  // Single template GET/PUT hooks
  useGetAdminNoteTemplateQuery,
  useGetAdminNoteTemplatesQuery,
  useGetAdminOrgNoteTemplatesQuery,
  // Provider templates hook (what provider actually sees)
  useGetAdminProviderTemplatesQuery,
  useGetAdminTemplateRevisionQuery,
  // Revision history hooks
  useGetAdminTemplateRevisionsQuery,
  useGetAdminUserNoteTemplatesQuery,
  useLazyGetAdminNoteTemplateQuery,
  useLazyGetAdminNoteTemplatesQuery,
  useLazyGetAdminOrgNoteTemplatesQuery,
  useLazyGetAdminTemplateRevisionQuery,
  useLazyGetAdminTemplateRevisionsQuery,
  useLazyGetAdminUserNoteTemplatesQuery,
  useUpdateAdminNoteTemplateMutation,
} from './noteTemplatesAdminApi';

// Custom hooks for operation tracking
import { useCallback, useEffect,useRef } from 'react';

export interface OperationProgress {
  operationId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: {
    imported_count?: number;
    failed_count?: number;
    skipped_count?: number;
    total_count?: number;
  };
  error?: string;
  completedAt?: string;
}

export interface UseImportOperationTrackingOptions {
  sessionId: string;
  onOperationComplete?: (operation: OperationProgress) => void;
  onOperationError?: (operation: OperationProgress) => void;
  pollingInterval?: number;
}

export const useImportOperationTracking = ({
  sessionId,
  onOperationComplete,
  onOperationError,
  pollingInterval = 2000,
}: UseImportOperationTrackingOptions) => {
  const activeOperations = useRef<Set<string>>(new Set());
  const pollingTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Get the operation status query hook
  const [getOperationStatus] = adminApi.useLazyGetImportOperationStatusQuery();

  const startTracking = useCallback((operationId: string) => {
    if (activeOperations.current.has(operationId)) {
      return; // Already tracking
    }

    activeOperations.current.add(operationId);

    const poll = async () => {
      try {
        const result = await getOperationStatus({
          sessionId,
          operationId,
        }).unwrap();

        const operation: OperationProgress = {
          operationId,
          status: result.status as OperationProgress['status'],
          progress: {
            imported_count: result.results_imported_count,
            failed_count: result.results_failed_count,
            skipped_count: result.results_skipped_count,
            total_count: result.progress_total_count,
          },
          error: result.error_details?.[0]?.message || undefined,
          completedAt: result.completed_at || undefined,
        };

        if (result.status === 'completed') {
          // Operation completed successfully
          activeOperations.current.delete(operationId);
          const timer = pollingTimers.current.get(operationId);
          if (timer) {
            clearTimeout(timer);
            pollingTimers.current.delete(operationId);
          }
          onOperationComplete?.(operation);
        } else if (result.status === 'failed') {
          // Operation failed
          activeOperations.current.delete(operationId);
          const timer = pollingTimers.current.get(operationId);
          if (timer) {
            clearTimeout(timer);
            pollingTimers.current.delete(operationId);
          }
          onOperationError?.(operation);
        } else {
          // Still processing, schedule next poll
          const timer = setTimeout(() => {
            void poll();
          }, pollingInterval);
          pollingTimers.current.set(operationId, timer);
        }
      } catch (error) {
        console.error('Error polling operation status:', error);
        // Continue polling on error - the operation might still be running
        const timer = setTimeout(() => {
          void poll();
        }, pollingInterval);
        pollingTimers.current.set(operationId, timer);
      }
    };

    // Start polling immediately
    poll();
  }, [sessionId, getOperationStatus, onOperationComplete, onOperationError, pollingInterval]);

  const stopTracking = useCallback((operationId: string) => {
    activeOperations.current.delete(operationId);
    const timer = pollingTimers.current.get(operationId);
    if (timer) {
      clearTimeout(timer);
      pollingTimers.current.delete(operationId);
    }
  }, []);

  const stopAllTracking = useCallback(() => {
    pollingTimers.current.forEach(timer => clearTimeout(timer));
    pollingTimers.current.clear();
    activeOperations.current.clear();
  }, []);

  const isTracking = useCallback((operationId: string) => {
    return activeOperations.current.has(operationId);
  }, []);

  const getActiveOperations = useCallback(() => {
    return Array.from(activeOperations.current);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllTracking();
    };
  }, [stopAllTracking]);

  return {
    startTracking,
    stopTracking,
    stopAllTracking,
    isTracking,
    getActiveOperations,
  };
};
